//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  InfiniteData,
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import {
  createCollection,
  getCollections,
} from '@apis/collections/collectionsApi';
import { ICollectionCreateRequest } from '@apis/collections/iCollectionCreateRequest';
import { getDeetsItemDetails } from '@apis/content-library/contentLibraryApi';
import { IContentLibraryItemsResponse } from '@apis/content-library/contentLibraryApiModel';
import { getCompetitors } from '@apis/instagram-competitors/instagramCompetitorsApi';
import { getConnectedMultiHandleInstagramAccount } from '@apis/instagram-settings/gatewayInstagramSettingsApi';
import { IGetConnectedInstagramAccountsResponse } from '@apis/instagram-settings/gatewayInstagramSettingsApiModel';
import { getInstagramSettings } from '@apis/instagram-settings/instagramSettingsApi';
import { IGetInstagramSettingsResponse } from '@apis/instagram-settings/instagramSettingsApiModel';
import { batchUpdateModerationStatus } from '@apis/moderation/moderationApi';
import {
  ModerationStatus,
  ModerationStatusInfo,
  ModerationStatusSource,
} from '@apis/moderation/moderationApiModel';
import { getUser } from '@apis/pinterest-user/pinterestUserApi';
import { getAllUsers } from '@apis/team-management/teamManagementApi';
import { getConnectionStatus } from '@apis/tiktok-settings/tiktokSettingsApi';
import { getAllTrackedSources } from '@apis/tracked-sources/trackedSourcesApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { clientFilter } from '@media-library/utils/mediaLibraryUtils';
import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { pluralize } from '@utils/stringUtils';

export const useGetTeamMembersQuery = (
  clientId: ClientId,
  clientName: string,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.MediaLibrary,
      QueryKeyConstants.MediaLibraryTeamManagementUsers,
    ],
    queryFn: ({ signal }) => getAllUsers({ signal }),
    select: (data) => {
      // Return all users if client name is 'All Brands'. Otherwise, return all basic users that do not have `clientIds` property and admin users whose `clientIds` includes the current client's id.
      return clientFilter(clientId, clientName, data);
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    placeholderData: keepPreviousData,
  });
};

const useGetInstagramSettingsBaseQuery = <T>(
  select: (data: IGetInstagramSettingsResponse) => T,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.MediaLibrary,
      QueryKeyConstants.MediaLibraryInstagram,
      QueryKeyConstants.MediaLibraryInstagramSettings,
    ],
    queryFn: ({ signal }) => getInstagramSettings({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useGetInstagramSettingsQuery = () => {
  const select = (data: IGetInstagramSettingsResponse) => data;

  return useGetInstagramSettingsBaseQuery(select);
};

export const useGetInstagramPrimaryAccountQuery = () => {
  const select = (data: IGetInstagramSettingsResponse) => {
    return data.accounts.find(
      ({ accountId }) => accountId === data.primaryAccountId,
    );
  };

  return useGetInstagramSettingsBaseQuery(select);
};

export const useGetInstagramTrackedCompetitorsQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.MediaLibrary,
      QueryKeyConstants.MediaLibraryInstagram,
      QueryKeyConstants.MediaLibraryInstagramSettings,
      QueryKeyConstants.MediaLibraryInstagramTrackedCompetitors,
    ],
    queryFn: ({ signal }) => getCompetitors({ signal }),
    staleTime: FIVE_MINUTES_STALE_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useGetInstagramTrackedUsersAndHashtagsQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.MediaLibrary,
      QueryKeyConstants.MediaLibraryInstagram,
      QueryKeyConstants.MediaLibraryInstagramSettings,
      QueryKeyConstants.MediaLibraryInstagramTrackedUsersAndHashtags,
    ],
    queryFn: ({ signal }) => getAllTrackedSources({ signal }),
    staleTime: FIVE_MINUTES_STALE_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useGetPinterestUserQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.MediaLibrary,
      QueryKeyConstants.MediaLibraryPinterestUser,
    ],
    queryFn: async ({ signal }) => {
      const pinterestUser = await getUser({ signal });
      return { pinterestUser }; // react-query does not allow return undefined
    },
    select: (data) => data.pinterestUser,
    staleTime: FIVE_MINUTES_STALE_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useGetAllCollectionsQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.MediaLibrary,
      QueryKeyConstants.MediaLibraryCollections,
    ],
    queryFn: ({ signal }) => getCollections(undefined, { signal }),
    placeholderData: keepPreviousData,
  });
};

export const useCreateCollectionMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (req: ICollectionCreateRequest) => {
      return createCollection(req);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.MediaLibrary,
          QueryKeyConstants.MediaLibraryCollections,
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Collection Created',
          message: 'Your collection has been successfully created.',
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Error Creating Collection',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
};

// Multi Handle mentioned & tagged
export const useGetInstagramConnectedAccountsQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.MediaLibrary,
      QueryKeyConstants.MediaLibraryInstagram,
      QueryKeyConstants.InstgramMultiHandleConnectedAccounts,
    ],
    queryFn: ({ signal }) =>
      getConnectedMultiHandleInstagramAccount({ signal }),
    select: (connectedAccounts: IGetConnectedInstagramAccountsResponse) =>
      connectedAccounts.data.connectedAccounts,
    staleTime: FIVE_MINUTES_STALE_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useGetTikTokConnectionStatusQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.MediaLibrary,
      QueryKeyConstants.MediaLibraryTikTok,
      QueryKeyConstants.MediaLibraryTikTokSettings,
    ],
    queryFn: ({ signal }) => getConnectionStatus(),
    staleTime: FIVE_MINUTES_STALE_TIME,
    placeholderData: keepPreviousData,
  });
};

interface UpdateModerationStatusParams {
  cuids: Cuid[];
  needsAction?: boolean;
  status: ModerationStatus;
}

const MODERATION_MEDIA_QUERY_KEYS = [
  QueryKeyConstants.MediaFilters,
  QueryKeyConstants.MediaFiltersMediaItems,
] as const;

export const useUpdateModerationStatusMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cuids,
      needsAction,
      status,
    }: UpdateModerationStatusParams) => {
      const contents: ModerationStatusInfo[] = cuids.map((cuid) => ({
        cuid,
        status,
        statusSource: ModerationStatusSource.UserOverride,
      }));

      await batchUpdateModerationStatus({ contents });

      return { count: cuids.length, status, needsAction };
    },
    onMutate: async ({ cuids }) => {
      await queryClient.cancelQueries({
        queryKey: MODERATION_MEDIA_QUERY_KEYS,
      });

      const itemIds = new Set(cuids.map((cuid) => Cuid.parse(cuid).itemId));

      // Optimistically removes items from all content library infinite query caches
      queryClient.setQueriesData<InfiniteData<IContentLibraryItemsResponse>>(
        {
          queryKey: MODERATION_MEDIA_QUERY_KEYS,
        },
        (data) => {
          if (!data) {
            return data;
          }

          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              contentItems: page.contentItems.filter(
                (item) => !itemIds.has(item.id),
              ),
            })),
          };
        },
      );
    },
    onSuccess: ({ count, needsAction, status }) => {
      const restoredToast = {
        title: 'Content restored',
        message: needsAction
          ? 'It has been moved to Needs Action.'
          : 'It has been moved to Ready to Distribute.',
        type: ToastType.SUCCESS,
        options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
      };

      const rejectedToast = {
        title: 'Content rejected',
        message:
          count > 1
            ? `${pluralize(count, 'item')} have been moved to Rejected.`
            : `It has been moved to Rejected.`,
        type: ToastType.SUCCESS,
        options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
      };

      dispatch(
        toastNotificationsActions.addToastNotification(
          status === ModerationStatus.Approved ? restoredToast : rejectedToast,
        ),
      );
    },
    onError: (_, { cuids: __, status }) => {
      const action =
        status === ModerationStatus.Rejected ? 'reject' : 'restore';
      const actionVerb =
        status === ModerationStatus.Rejected ? 'rejecting' : 'restoring';

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: `Failed to ${action} content`,
          message: `Something went wrong while ${actionVerb} the content. Please try again or contact Bazaarvoice Support.`,
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: MODERATION_MEDIA_QUERY_KEYS,
        refetchType: 'all',
      });
    },
  });
};

export const useDeetsItemDetails = (itemIds: PartialCuid[]) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.MediaLibrary,
      QueryKeyConstants.MediaLibraryMediaItems,
      QueryKeyConstants.MediaLibraryMediaItemsDeetsMetadata,
      itemIds,
    ],
    queryFn: async ({ signal }) => {
      return getDeetsItemDetails(itemIds, { signal });
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};
